/**
 * Responsibility: Implement json utils behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
import { createAIError } from './errors'
// ============================================================
// AI/JSON-UTILS.TS — Robust JSON extraction from AI responses
// ============================================================

/** Walk character-by-character to find the correctly balanced outermost JSON object. */
function findJsonObject(text: string): string | null {
  const start = text.indexOf('{')
  if (start === -1) return null
  let depth = 0
  let inString = false
  let escape = false
  for (let i = start; i < text.length; i++) {
    const c = text[i]
    if (escape)              { escape = false; continue; }
    if (c === '' && inString) { escape = true; continue; }
    if (c === '"')           { inString = !inString; continue; }
    if (inString)            continue
    if (c === '{') depth++
    if (c === '}') { if (--depth === 0) return text.slice(start, i + 1); }
  }
  return null
}

export function extractJson(raw: string): unknown {
  // 1. Strip any markdown fences (handles ` ```json ... ``` `)
  const clean = raw.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim()
  // 2. Direct parse (happy path)
  try { return JSON.parse(clean); } catch { /* fall through */ }

  // 3. Balanced-brace extraction — handles leading/trailing prose and prose
  //    containing { } characters (lastIndexOf would pick the wrong closing brace)
  const slice = findJsonObject(clean)
  if (slice) {
    try { return JSON.parse(slice); } catch { /* fall through */ }

    // 4. Repair trailing commas  e.g.  { "a": 1, }
    const noTrailing = slice.replace(/,(\s*[}\]])/g, '$1')
    try { return JSON.parse(noTrailing); } catch { /* fall through */ }
  }

  // 5. Strip internal markdown blocks that may appear mid-JSON (e.g. ```json\n{...}\n```)
  const deepClean = clean.replace(/```[\s\S]*?```/g, '')
  const slice2 = findJsonObject(deepClean)
  if (slice2) {
    try { return JSON.parse(slice2); } catch { /* fall through */ }
    const noTrailing2 = slice2.replace(/,(\s*[}\]])/g, '$1')
    try { return JSON.parse(noTrailing2); } catch { /* fall through */ }
  }

  console.warn('[extractJson] unparseable response:', raw.slice(0, 300))
  throw createAIError('response-invalid-json', {
    hint: 'Ask the model to return valid JSON only, or switch to a stronger model for this job.',
  })
}
